import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { updateCurrentUser } from '../api/client'
import type { UserProfile } from '../api/types'
import { SUPPORTED_LANGUAGES, LANGUAGE_STORAGE_KEY, type Language } from '../i18n'

function isLanguage(val: unknown): val is Language {
  return typeof val === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(val)
}

/**
 * Maps the backend grading `feedback_language` (e.g. "Turkish", "English") to a
 * UI locale. Used only as a last-resort fallback when neither DB nor localStorage
 * holds a preference.
 */
export function feedbackLanguageToUi(feedbackLanguage?: string | null): Language {
  switch ((feedbackLanguage || '').trim().toLowerCase()) {
    case 'turkish':
    case 'türkçe':
    case 'tr':
      return 'tr'
    case 'dutch':
    case 'nederlands':
    case 'nl':
      return 'nl'
    default:
      return 'en'
  }
}

/**
 * Read the language the user last chose before logging in (stored by i18next's
 * localStorage detector). Returns null if nothing was stored or the value is not
 * a supported locale.
 */
function getLocalStorageLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    return isLanguage(stored) ? stored : null
  } catch {
    return null
  }
}

export function useUiLanguage() {
  const { i18n } = useTranslation()
  const current = (isLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en') as Language

  // Change the active UI language and persist the choice to the backend.
  // i18next's localStorage detector caches the value locally, so anonymous
  // users (no session) still keep their choice; the DB write is best-effort.
  const setLanguage = useCallback(
    async (lng: Language) => {
      if (!isLanguage(lng)) return
      await i18n.changeLanguage(lng)
      try {
        await updateCurrentUser({ ui_language: lng })
      } catch {
        // Not logged in or request failed — localStorage already holds the choice.
      }
    },
    [i18n],
  )

  // Reconcile the active language with the authenticated user's stored preference.
  //
  // Priority order:
  //   1. DB value (`ui_language`) — explicit saved preference, always wins.
  //   2. localStorage — the user may have chosen a language before logging in
  //      (e.g. on the Landing page). Persist that choice to DB so it's remembered.
  //   3. feedback_language mapping — last resort for brand-new users with no
  //      localStorage preference either.
  //
  // This function is called once per session on Header mount after /api/me resolves.
  const syncFromUser = useCallback(
    async (user: Pick<UserProfile, 'ui_language' | 'feedback_language'> | null | undefined) => {
      if (!user) return

      // 1. DB has an explicit value → honour it, apply if different from active.
      if (isLanguage(user.ui_language)) {
        if (user.ui_language !== i18n.resolvedLanguage) {
          await i18n.changeLanguage(user.ui_language)
        }
        return
      }

      // 2. No DB value — check if the user already chose via localStorage (pre-login).
      const localLng = getLocalStorageLanguage()
      if (localLng) {
        // Apply if not already active (i18next may have already loaded it).
        if (localLng !== i18n.resolvedLanguage) {
          await i18n.changeLanguage(localLng)
        }
        // Persist the localStorage choice to DB so future logins use it without
        // re-detecting from localStorage.
        try {
          await updateCurrentUser({ ui_language: localLng })
        } catch {
          // best-effort
        }
        return
      }

      // 3. Nothing in localStorage either — seed from feedback_language as a
      //    reasonable default and save it.
      const seeded = feedbackLanguageToUi(user.feedback_language)
      if (seeded !== i18n.resolvedLanguage) {
        await i18n.changeLanguage(seeded)
      }
      try {
        await updateCurrentUser({ ui_language: seeded })
      } catch {
        // best-effort
      }
    },
    [i18n],
  )

  return { language: current, setLanguage, syncFromUser }
}

